import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface LegalContent {
  cgv: string;
  mentions_legales: string;
  politique_confidentialite: string;
  cookies_policy: string;
}

const LegalPage = () => {
  const [content, setContent] = useState<LegalContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLegalContent = async () => {
      try {
        const { data, error } = await supabase
          .from('legal_versions')
          .select('cgv, mentions_legales, politique_confidentialite, cookies_policy')
          .eq('is_current', true)
          .maybeSingle();

        if (error) throw error;
        
        if (data) {
          setContent(data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement du contenu légal:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLegalContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 container mx-auto py-8 px-4">
          <p>Chargement...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 container mx-auto py-8 px-4">
          <p>Aucun contenu légal disponible pour le moment.</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 container mx-auto py-8 px-4">
        <h1 className="text-3xl font-playfair text-villa-blue mb-8">Informations Légales</h1>

        <Tabs defaultValue="cgv" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="cgv">CGV</TabsTrigger>
            <TabsTrigger value="mentions">Mentions Légales</TabsTrigger>
            <TabsTrigger value="confidentialite">Confidentialité</TabsTrigger>
            <TabsTrigger value="cookies">Cookies</TabsTrigger>
          </TabsList>

          <TabsContent value="cgv" className="prose max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-playfair text-villa-blue mb-4">Conditions Générales de Vente</h2>
              <div className="whitespace-pre-wrap">{content.cgv}</div>
            </section>
          </TabsContent>

          <TabsContent value="mentions" className="prose max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-playfair text-villa-blue mb-4">Mentions Légales</h2>
              <div className="whitespace-pre-wrap">{content.mentions_legales}</div>
            </section>
          </TabsContent>

          <TabsContent value="confidentialite" className="prose max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-playfair text-villa-blue mb-4">Politique de Confidentialité</h2>
              <div className="whitespace-pre-wrap">{content.politique_confidentialite}</div>
            </section>
          </TabsContent>

          <TabsContent value="cookies" className="prose max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-playfair text-villa-blue mb-4">Politique des Cookies</h2>
              <div className="whitespace-pre-wrap">{content.cookies_policy}</div>
              
              <button 
                className="text-villa-turquoise underline cursor-pointer mt-6 block"
                onClick={() => window.dispatchEvent(new Event("open-cookie-preferences"))}
              >
                Gérer mes préférences de cookies
              </button>
            </section>
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
};

export default LegalPage;
